import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { RenderDelegate } from './renderSequence';
export enum AccountStatus {
  Verified = 'VERIFIED',
  Unverified = 'UNVERIFIED',
  Unknown = 'UNKNOWN',
}

export interface TenantAdminPostRequestDto {
  tenantId: string;
  email: string;
}

export type UserInfoDto = Pick<UserDto, 'id' | 'email' | 'givenName' | 'familyName' | 'avatar'>;

export interface UserDto {
  id: string;
  givenName?: string;
  familyName?: string;
  email: string;
  accountStatus?: AccountStatus;
  groups: string[];
  permissions: string[];
  createdOn?: string;
  modifiedOn?: string;
  isExternal?: boolean;
  notListable?: boolean;
  avatar?: string;
  highResAvatar?: string;
  groupLabels?: string[];
  extGroupsHint: string[];
  primaryGroupDefaultUploadLocationId?: string;
  /**
   * A default render location on the **user's primary group**. During sequence export,
   * the default render location (Kelda location) will be pre-selected as the render location.
   */
  primaryGroupKeldaIdForConformLocation: string | undefined;
  primaryGroupRenderDelegateForMimirCutter: RenderDelegate | undefined;
  /**
   * A default bookmark for the user's primary group. If set, this bookmark
   * will be shown when users with this primary group click the Mimir logo
   * or first log in. If not set, the system default search page is shown.
   */
  primaryGroupDefaultBookmarkId: string | undefined | null;
  isDeactivated?: boolean;

  /**
   * Indicates whether the user prefers to use software token for MFA.
   *
   * Note: do not set this value for external users (those who are logged in via SSO).
   */
  isSoftwareTokenMfaEnabled: boolean | undefined;
}

export default UserDto;

export type UpdateUserDto = Pick<UserDto, 'id'> & Partial<UserDto>;

/**
 * UserUpdateDto is created by SignUpContinue as part of an AccountUpdate event
 * to signal that a user account should be updated.
 */
export interface VerifyUserDto extends Required<Pick<UserDto, 'givenName' | 'familyName' | 'accountStatus'>> {
  accountStatus: AccountStatus.Verified
}

export interface UserModelView extends UserDto {
  isTenantAdmin: boolean;
  isSuperAdmin: boolean;
  loadingDelete: boolean;
  loadingUpdate: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  groupSelectionChanged: boolean;
}

export const newUserModel = (): UserModelView => {
  return {
    id: '',
    email: '',
    accountStatus: AccountStatus.Unknown,
    groups: [],
    permissions: [],
    isTenantAdmin: false,
    isSuperAdmin: false,
    loadingDelete: false,
    loadingUpdate: false,
    isSaving: false,
    isDeleting: false,
    groupSelectionChanged: false,
    extGroupsHint: [],
    primaryGroupKeldaIdForConformLocation: undefined,
    primaryGroupRenderDelegateForMimirCutter: undefined,
    isSoftwareTokenMfaEnabled: undefined,
    primaryGroupDefaultBookmarkId: undefined,
  };
};

export interface CreateUserDto {
  email: string,
  groups: string[]
}

export const searchInUser = (user: UserInfoDto, searchText: string | null) => {
  if (!searchText) return true;
  const queryTextLC = searchText.toLowerCase();
  const emailMatch = user.email.toLowerCase().includes(searchText.toLowerCase());
  const nameMatch = user.givenName && user.familyName && `${user.givenName} ${user.familyName}`.toLowerCase().includes(queryTextLC);
  return emailMatch || nameMatch;
};

export const getUserDisplayName = (user: UserInfoDto | null) => {
  if (!user) return 'Unknown user';
  let label = '';
  if (user.givenName) {
    label = user.givenName;
  }
  if (user.familyName) {
    label += label.length === 0 ? user.familyName : ` ${user.familyName}`;
  }
  return label.length === 0 ? user.email : label;
};

export const isInstanceOfUserDto = (obj: unknown): obj is UserDto => {
  return isCreateUserDto(obj) && 'id' in obj;
};

export const isCreateUserDto = (obj: unknown): obj is CreateUserDto => {
  return isObject(obj) && 'email' in obj && 'groups' in obj && Array.isArray(obj['groups']);
};

export const isInstanceOfUserModelView = (obj: unknown): obj is UserModelView => {
  return isObject(obj) && 'id' in obj;
};

export const isInstanceOfUserModelCollection = (obj: unknown): obj is UserModelView[] => {
  if (!Array.isArray(obj)) return false;
  if (obj.length > 0) return isInstanceOfUserModelView(obj[0]);
  return true;
};

export const isInstanceOfTenantAdminPostRequestDto = (obj: unknown): obj is TenantAdminPostRequestDto => {
  return isObject(obj) && 'email' in obj && 'tenantId' in obj;
};

export type InternalUserInfoResponse = {
  tenantId: string,
  id: string,
  email: string,
  groups?: string[],
  permissions?: string[],
  givenName?: string,
  familyName?: string,
};

/**
 * The data returned by our internal userInfo lambda that
 * we use for cross stack user info lookup.
 */
export type InternalUserInfoResponseDto = InternalUserInfoResponse[];

type InternalUserInfoRequestCommonParams = { withGroupsAndPermissions: boolean, withName: boolean };
type InternalUserInfoRequestByUserId = { userIds: string[] } & InternalUserInfoRequestCommonParams;
type InternalUserInfoRequestByEmail = { userEmails: string[], tenantId: string } & InternalUserInfoRequestCommonParams;
export type InternalUserInfoRequestByTenant = { tenantId: string } & InternalUserInfoRequestCommonParams;

export type InternalUserInfoRequestDto = InternalUserInfoRequestByUserId | InternalUserInfoRequestByEmail | InternalUserInfoRequestByTenant;

export interface UserStatusDto {
  isDeactivated: boolean;
}

